import { ChangeEvent, DragEvent, type CSSProperties, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAppState } from "../state/AppState";
import electronicsImage from "../assets/home-orbit/electronics.png";
import printerImage from "../assets/home-orbit/printer.png";
import raceCarImage from "../assets/home-orbit/race-car.png";

type Stage = {
  id: string;
  title: string;
  accent: string;
  copy: string;
  image: string;
  imageClass: string;
};

const stages: Stage[] = [
  {
    id: "printer",
    title: "Upload the idea.",
    accent: "We'll print the rest.",
    copy: "Upload your 3D model, choose the material and finish, and get a clear production quote for your part.",
    image: printerImage,
    imageClass: "orbit-home-object--printer"
  },
  {
    id: "race-car",
    title: "Crashed it? Print it.",
    accent: "Race again.",
    copy: "Replace brackets, mounts, ducts and one-off racing components without waiting on traditional manufacturing.",
    image: raceCarImage,
    imageClass: "orbit-home-object--car"
  },
  {
    id: "electronics",
    title: "Your electronics deserve",
    accent: "a better enclosure.",
    copy: "Create precise housings, fixtures and prototype enclosures for PCB assemblies and electronics projects.",
    image: electronicsImage,
    imageClass: "orbit-home-object--electronics"
  }
];

export function HomePage() {
  const navigate = useNavigate();
  const { setPendingFiles, theme } = useAppState();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const targetProgressRef = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let scrollFrame = 0;
    let motionFrame = 0;
    let snapTimer = 0;

    const updateProgress = () => {
      const cycleHeight = Math.max(900, window.innerHeight * 1.55);
      const cycleCount = window.scrollY / cycleHeight;
      targetProgressRef.current = wrapProgress(cycleCount);

      if (window.scrollY > cycleHeight * 30) {
        window.scrollTo({ top: cycleHeight * (3 + wrapProgress(cycleCount)), behavior: "auto" });
      }
    };

    const snapToNearestStage = () => {
      const cycleHeight = Math.max(900, window.innerHeight * 1.55);
      const step = 1 / stages.length;
      const cycleCount = window.scrollY / cycleHeight;
      const snapCycle = Math.round(cycleCount / step) * step;
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      window.scrollTo({
        top: cycleHeight * snapCycle,
        behavior: prefersReducedMotion ? "auto" : "smooth"
      });
    };

    const tick = () => {
      setProgress((current) => wrapProgress(current + shortestLoopDistance(targetProgressRef.current, current) * 0.14));
      motionFrame = window.requestAnimationFrame(tick);
    };

    const onScroll = () => {
      window.cancelAnimationFrame(scrollFrame);
      window.clearTimeout(snapTimer);
      scrollFrame = window.requestAnimationFrame(updateProgress);
      snapTimer = window.setTimeout(snapToNearestStage, 180);
    };

    updateProgress();
    motionFrame = window.requestAnimationFrame(tick);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.cancelAnimationFrame(scrollFrame);
      window.cancelAnimationFrame(motionFrame);
      window.clearTimeout(snapTimer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  function handleFiles(nextFiles: FileList | File[]) {
    const accepted = Array.from(nextFiles).filter((file) => file.name.toLowerCase().endsWith(".stl"));
    if (!accepted.length) return;
    setPendingFiles(accepted);
    navigate("/quote");
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files.length) handleFiles(event.dataTransfer.files);
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    if (event.target.files?.length) handleFiles(event.target.files);
  }

  const loopProgress = wrapProgress(progress);

  return (
    <section
      ref={scrollAreaRef}
      className={theme === "dark" ? "orbit-home-scroll orbit-home-scroll--dark" : "orbit-home-scroll"}
      style={{ "--orbit-spin": `${(loopProgress - 0.5) * 6}deg` } as CSSProperties}
    >
      <div className="orbit-home-hero">
        <div className="orbit-home-backgrounds" aria-hidden="true">
          {stages.map((stage, index) => (
            <div
              key={stage.id}
              className={`orbit-home-background orbit-home-background--${stage.id}`}
              style={{ opacity: stagePresence(loopProgress, index) }}
            />
          ))}
        </div>

        <div className="orbit-home-grain" aria-hidden="true" />

        <div className="orbit-home-copy-stack">
          <div className="orbit-home-copy-window">
            {stages.map((stage, index) => (
              <article
                key={stage.id}
                className="orbit-home-copy"
                style={copyStyle(loopProgress, index)}
              >
                <h1>
                  {stage.title}
                  <br />
                  <em>{stage.accent}</em>
                </h1>
                <p>{stage.copy}</p>
              </article>
            ))}
          </div>

          <div
            className={dragging ? "orbit-home-upload is-dragging" : "orbit-home-upload"}
            id="home-dropzone"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <span className="orbit-home-panel-accent orbit-home-panel-accent--top" aria-hidden="true" />
            <span className="orbit-home-panel-accent orbit-home-panel-accent--bottom" aria-hidden="true" />
            <div className="orbit-home-upload-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 15.5v2.25A2.25 2.25 0 0 0 7.25 20h9.5A2.25 2.25 0 0 0 19 17.75V15.5" />
              </svg>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".stl"
              onClick={(event) => event.stopPropagation()}
              onChange={handleInputChange}
              aria-label="Upload 3D model"
            />
            <strong>Upload your model</strong>
            <small>3D models · maximum 50 MB</small>
          </div>

          <div className="orbit-home-actions">
            <Button variant="outline" size="lg" className="orbit-home-action orbit-home-catalog" asChild>
              <Link to="/products">Browse Catalog</Link>
            </Button>
            <Button variant="secondary" size="lg" className="orbit-home-action orbit-home-faq" asChild>
              <Link to="/faq">Have Questions?</Link>
            </Button>
          </div>
        </div>

        <div className="orbit-home-stage" aria-hidden="true">
          <div className="orbit-home-orbit" />
          {stages.map((stage, index) => (
            <div
              key={stage.id}
              className={`orbit-home-object ${stage.imageClass}`}
              style={objectStyle(loopProgress, index)}
            >
              <span className="orbit-home-shadow" />
              <img src={stage.image} alt="" draggable="false" />
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

function stagePresence(progress: number, index: number) {
  const center = stageCenter(index);
  const distance = Math.abs(shortestLoopDistance(center, progress));
  return Math.max(0, 1 - smoothstep(0.12, 0.34, distance));
}

function copyStyle(progress: number, index: number): CSSProperties {
  const center = stageCenter(index);
  const distance = -shortestLoopDistance(center, progress);
  const visible = stagePresence(progress, index);
  return {
    opacity: visible,
    transform: `translateY(${distance * -84}px)`,
    filter: `blur(${(1 - visible) * 4}px)`
  };
}

function objectStyle(progress: number, index: number): CSSProperties {
  const center = stageCenter(index);
  const distance = shortestLoopDistance(center, progress);
  const visible = Math.max(0, 1 - smoothstep(0.08, 0.3, Math.abs(distance)));
  const x = distance * 560;
  const y = distance * 310;
  const scale = 0.76 + visible * 0.24;
  const rotate = distance * -16;
  return {
    opacity: visible,
    transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) rotate(${rotate}deg) scale(${scale})`
  };
}

function smoothstep(edge0: number, edge1: number, value: number) {
  const x = Math.min(1, Math.max(0, (value - edge0) / (edge1 - edge0)));
  return x * x * (3 - 2 * x);
}

function wrapProgress(value: number) {
  return ((value % 1) + 1) % 1;
}

function stageCenter(index: number) {
  return index / stages.length;
}

function shortestLoopDistance(center: number, progress: number) {
  let distance = center - progress;
  if (distance > 0.5) distance -= 1;
  if (distance < -0.5) distance += 1;
  return distance;
}
